/**
 * MANAGE ITEMS MODAL COMPONENT
 * 
 * SUMMARY:
 * Modal component for selectively managing which subcontractor items from selected tasks
 * appear in the Work Order table. Provides checkbox-based multi-select interface with
 * grouping by task and subtask for clear organization.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is visible
 * @param {function} props.onClose - Handler for closing modal
 * @param {Array} props.availableItems - All subcontractor items from selected tasks
 * @param {Set} props.checkedItemsSet - Set of checked item keys
 * @param {function} props.onToggleItem - Handler for toggling individual item
 * @param {function} props.onApply - Handler for applying selection changes
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Modal from '../modal/modal';
import './ManageItemsModal.scss';

const ManageItemsModal = ({
  open,
  onClose,
  availableItems,
  checkedItemsSet,
  onToggleItem,
  onApply,
}) => {
  /**
   * Groups available items by stage → task → subtask → items for hierarchical display.
   * Structure: { [stageId]: { stageName, tasks: { [taskId]: { taskName, subtasks: { [subTaskId]: { items: [] } } } } } }
   */
  const groupedItems = useMemo(() => {
    const grouped = {};

    availableItems.forEach(item => {
      const hasStageAttribution = item.originalTaskId && item.originalTaskName && item.taskName !== item.originalTaskName;

      if (hasStageAttribution) {
        // Three-level grouping: Stage → Task → Subtask → Items
        const stageKey = item.taskId; // This is the stage ID (BASEMENT)
        const stageName = item.taskName; // This is the stage name (BASEMENT)
        const taskKey = item.originalTaskId; // This is the original task ID (WATER/FIRE INTERNAL)
        const taskName = item.originalTaskName; // This is the original task name

        // Use full stage path if available (e.g., "LEVEL 5 > SETOUT"), otherwise just stage name
        const stageDisplayName = item.stagePath || stageName;

        // Create stage group if it doesn't exist
        if (!grouped[stageKey]) {
          grouped[stageKey] = {
            stageName: stageName,
            stageDisplayName: stageDisplayName,
            stageId: stageKey,
            isStageGroup: true,
            tasks: {}
          };
        }

        // Create task group within stage if it doesn't exist
        if (!grouped[stageKey].tasks[taskKey]) {
          grouped[stageKey].tasks[taskKey] = {
            taskName: taskName,
            taskId: taskKey,
            subtasks: {}
          };
        }

        // Create subtask group within task if it doesn't exist
        if (!grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId]) {
          grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId] = {
            subTaskName: item.subTaskName,
            subTaskId: item.subTaskId,
            items: []
          };
        }

        // Add item to the subtask
        grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId].items.push(item);
      } else {
        // For non-attributed items: check if they have a parent stage path

        if (item.stagePath && item.stagePath.includes(' > ')) {
          // Has nested parent (e.g., "ROOF > STORMWATER")
          // Create three-level: Stage → Task → Subtask → Items
          const pathParts = item.stagePath.split(' > ');
          const parentStageName = pathParts.slice(0, -1).join(' > '); // "ROOF"
          const taskNameFromPath = pathParts[pathParts.length - 1]; // "STORMWATER"

          // Use parent stage name as the grouping key to ensure all items under same parent group together
          const stageKey = `stage-${parentStageName}`;
          const taskKey = item.taskId;

          if (!grouped[stageKey]) {
            grouped[stageKey] = {
              stageName: parentStageName,
              stageDisplayName: parentStageName,
              stageId: stageKey,
              isStageGroup: true,
              tasks: {}
            };
          }

          if (!grouped[stageKey].tasks[taskKey]) {
            grouped[stageKey].tasks[taskKey] = {
              taskName: taskNameFromPath,
              taskId: taskKey,
              subtasks: {}
            };
          }

          if (!grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId]) {
            grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId] = {
              subTaskName: item.subTaskName,
              subTaskId: item.subTaskId,
              items: []
            };
          }

          grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId].items.push(item);
        } else if (item.stagePath && !item.stagePath.includes(' > ')) {
          // Has simple parent (e.g., stagePath = "ROOF")
          // This is a direct task under a section - treat as stage group with single task
          const parentStageName = item.stagePath; // "ROOF"
          const stageKey = `stage-${parentStageName}`;
          const taskKey = item.taskId;
          const taskName = item.taskName;

          if (!grouped[stageKey]) {
            grouped[stageKey] = {
              stageName: parentStageName,
              stageDisplayName: parentStageName,
              stageId: stageKey,
              isStageGroup: true,
              tasks: {}
            };
          }

          if (!grouped[stageKey].tasks[taskKey]) {
            grouped[stageKey].tasks[taskKey] = {
              taskName: taskName,
              taskId: taskKey,
              subtasks: {}
            };
          }

          if (!grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId]) {
            grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId] = {
              subTaskName: item.subTaskName,
              subTaskId: item.subTaskId,
              items: []
            };
          }

          grouped[stageKey].tasks[taskKey].subtasks[item.subTaskId].items.push(item);
        } else {
          // No stagePath - simple task grouping
          const taskKey = item.taskId;
          const taskName = item.taskName;

          if (!grouped[taskKey]) {
            grouped[taskKey] = {
              taskName: taskName,
              taskDisplayName: taskName,
              taskId: taskKey,
              isStageGroup: false,
              subtasks: {}
            };
          }

          if (!grouped[taskKey].subtasks[item.subTaskId]) {
            grouped[taskKey].subtasks[item.subTaskId] = {
              subTaskName: item.subTaskName,
              subTaskId: item.subTaskId,
              items: []
            };
          }

          grouped[taskKey].subtasks[item.subTaskId].items.push(item);
        }
      }
    });

    return grouped;
  }, [availableItems]);

  /**
   * Checks if all items in a subtask are checked.
   */
  const isSubtaskFullyChecked = (items) => {
    return items.every(item =>
      checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
    );
  };

  /**
   * Checks if all items in a task are checked.
   */
  const isTaskFullyChecked = (task) => {
    const allItems = Object.values(task.subtasks).flatMap(subtask => subtask.items);
    return allItems.every(item =>
      checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
    );
  };

  /**
   * Checks if all items in a stage are checked (for three-level grouping).
   */
  const isStageFullyChecked = (stage) => {
    const allItems = Object.values(stage.tasks)
      .flatMap(task => Object.values(task.subtasks))
      .flatMap(subtask => subtask.items);
    return allItems.every(item =>
      checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
    );
  };

  /**
   * Toggles all items within a stage (for three-level grouping).
   */
  const handleToggleStage = (stage) => {
    const allItems = Object.values(stage.tasks)
      .flatMap(task => Object.values(task.subtasks))
      .flatMap(subtask => subtask.items);
    const allChecked = isStageFullyChecked(stage);

    allItems.forEach(item => {
      const key = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
      if (allChecked) {
        onToggleItem(key, false);
      } else {
        onToggleItem(key, true);
      }
    });
  };

  /**
   * Toggles all items within a task.
   */
  const handleToggleTask = (task) => {
    const allItems = Object.values(task.subtasks).flatMap(subtask => subtask.items);
    const allChecked = isTaskFullyChecked(task);

    allItems.forEach(item => {
      const key = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
      if (allChecked) {
        onToggleItem(key, false);
      } else {
        onToggleItem(key, true);
      }
    });
  };

  /**
   * Toggles all items within a subtask.
   */
  const handleToggleSubtask = (subtask) => {
    const allChecked = isSubtaskFullyChecked(subtask.items);

    subtask.items.forEach(item => {
      const key = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
      if (allChecked) {
        onToggleItem(key, false);
      } else {
        onToggleItem(key, true);
      }
    });
  };

  const modalContent = (
    <div className="manage-items-modal-content">
      {Object.keys(groupedItems).length === 0 ? (
        <div className="empty-state">
          <Typography variant="body1" color="textSecondary">
            No subcontractor items available in selected tasks.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please select tasks with subcontractor items from the dropdown above.
          </Typography>
        </div>
      ) : (
        <div className="items-tree">
          {Object.values(groupedItems).map((group) => (
            group.isStageGroup ? (
              // Three-level: Stage → Task → Subtask → Items
              <Accordion key={group.stageId} defaultExpanded className="stage-accordion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className="stage-header"
                >
                  <Checkbox
                    checked={isStageFullyChecked(group)}
                    indeterminate={
                      !isStageFullyChecked(group) &&
                      Object.values(group.tasks).some(task =>
                        Object.values(task.subtasks).some(subtask =>
                          subtask.items.some(item =>
                            checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
                          )
                        )
                      )
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleStage(group);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="subtitle1" className="stage-name">
                    {group.stageDisplayName || group.stageName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="stage-content">
                  {Object.values(group.tasks).map((task) => (
                    <Accordion key={task.taskId} defaultExpanded className="task-accordion nested">
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className="task-header"
                      >
                        <Checkbox
                          checked={isTaskFullyChecked(task)}
                          indeterminate={
                            !isTaskFullyChecked(task) &&
                            Object.values(task.subtasks).some(subtask =>
                              subtask.items.some(item =>
                                checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
                              )
                            )
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleTask(task);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography variant="subtitle2" className="task-name nested">
                          {task.taskName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className="task-content">
                        {Object.values(task.subtasks).map((subtask) => (
                          <div key={subtask.subTaskId} className="subtask-group">
                            <div className="subtask-header">
                              <Checkbox
                                checked={isSubtaskFullyChecked(subtask.items)}
                                indeterminate={
                                  !isSubtaskFullyChecked(subtask.items) &&
                                  subtask.items.some(item =>
                                    checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
                                  )
                                }
                                onChange={() => handleToggleSubtask(subtask)}
                                size="small"
                              />
                              <Typography variant="body2" className="subtask-name">
                                {subtask.subTaskName}
                              </Typography>
                            </div>
                            <div className="items-list">
                              {subtask.items.map((item) => {
                                const itemKey = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
                                return (
                                  <div key={itemKey} className="item-row">
                                    <Checkbox
                                      checked={checkedItemsSet.has(itemKey)}
                                      onChange={() => onToggleItem(itemKey, !checkedItemsSet.has(itemKey))}
                                      size="small"
                                    />
                                    <Typography variant="body2" className="item-description">
                                      {item.subcontractorDescription}
                                    </Typography>
                                    <Typography variant="caption" className="item-cost">
                                      ${(item.cost ?? 0).toFixed(2)}
                                    </Typography>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            ) : (
              // Two-level: Task → Subtask → Items (backwards compatible for non-attributed items)
              <Accordion key={group.taskId} defaultExpanded className="task-accordion">
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className="task-header"
                >
                  <Checkbox
                    checked={isTaskFullyChecked(group)}
                    indeterminate={
                      !isTaskFullyChecked(group) &&
                      Object.values(group.subtasks).some(subtask =>
                        subtask.items.some(item =>
                          checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
                        )
                      )
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleTask(group);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography variant="subtitle1" className="task-name">
                    {group.taskDisplayName || group.taskName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="task-content">
                  {Object.values(group.subtasks).map((subtask) => (
                    <div key={subtask.subTaskId} className="subtask-group">
                      <div className="subtask-header">
                        <Checkbox
                          checked={isSubtaskFullyChecked(subtask.items)}
                          indeterminate={
                            !isSubtaskFullyChecked(subtask.items) &&
                            subtask.items.some(item =>
                              checkedItemsSet.has(`${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`)
                            )
                          }
                          onChange={() => handleToggleSubtask(subtask)}
                          size="small"
                        />
                        <Typography variant="subtitle2" className="subtask-name">
                          {subtask.subTaskName}
                        </Typography>
                      </div>
                      <div className="items-list">
                        {subtask.items.map((item) => {
                          const itemKey = `${item.taskId}-${item.subTaskId}-${item.subcontractorItemId}`;
                          return (
                            <div key={itemKey} className="item-row">
                              <Checkbox
                                checked={checkedItemsSet.has(itemKey)}
                                onChange={() => onToggleItem(itemKey, !checkedItemsSet.has(itemKey))}
                                size="small"
                              />
                              <Typography variant="body2" className="item-description">
                                {item.subcontractorDescription}
                              </Typography>
                              <Typography variant="caption" className="item-cost">
                                ${(item.cost ?? 0).toFixed(2)}
                              </Typography>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
            )
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalHeading="MANAGE WORK ORDER ITEMS"
      modalContent={modalContent}
      fullWidth={true}
      maxWidth="md"
      showActionBtn={true}
      modalCloseTxt="CANCEL"
      modalSaveTxt={`APPLY (${checkedItemsSet.size} ITEMS)`}
      onClick={onApply}
      data-cy="manage-items-modal"
    />
  );
};

ManageItemsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  availableItems: PropTypes.array.isRequired,
  checkedItemsSet: PropTypes.instanceOf(Set).isRequired,
  onToggleItem: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

export default ManageItemsModal;
